import { tokens, shell } from "views/Product/productPageStyle.js";

/**
 * Styles for the page a failed scan lands on.
 *
 * It shares the record page's shell and its design language — plaster ground,
 * one white sheet, hairlines, Newsreader over Inter over mono labels — because
 * it is the same journey: a reader who scanned a QR code either gets the
 * record or gets this. Two visual languages would read as two different
 * organisations.
 *
 * The one colour decision here: dried correction ink appears ONLY when the
 * server actually reported an invalid certification. The not-found and
 * unconfirmed states assert nothing, so they carry no alarm colour at all.
 * The old page had it the other way round — a pulsing red warning above copy
 * that said "this can happen with a typo".
 */
const {
  SHEET,
  INK,
  MUTED,
  RULE,
  GREEN,
  GREEN_HOVER,
  RED_INK,
  DISPLAY,
  BODY,
  MONO,
  DESKTOP,
} = tokens;

const invalidCertStyle = {
  ...shell,

  main: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
  },

  /* ------------------------------------------------------------ statement */
  statement: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "12px 0 34px",
    maxWidth: "720px",
    [DESKTOP]: { padding: "40px 0 56px", gap: "22px" },
  },
  eyebrow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    [DESKTOP]: { gap: "10px" },
  },
  eyebrowRule: {
    width: "18px",
    height: "1px",
    background: MUTED,
    flexShrink: 0,
    [DESKTOP]: { width: "26px" },
  },
  eyebrowRuleAlarm: { background: RED_INK },
  eyebrowText: {
    fontFamily: MONO,
    fontSize: "10px",
    fontWeight: 500,
    letterSpacing: "0.14em",
    lineHeight: "14px",
    color: MUTED,
    [DESKTOP]: { fontSize: "11px" },
  },
  eyebrowTextAlarm: { color: RED_INK },
  title: {
    fontFamily: DISPLAY,
    fontSize: "34px",
    fontWeight: 500,
    letterSpacing: "-0.02em",
    lineHeight: "38px",
    color: INK,
    margin: 0,
    [DESKTOP]: { fontSize: "52px", lineHeight: "56px", letterSpacing: "-0.025em" },
  },
  body: {
    fontFamily: BODY,
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "26px",
    color: INK,
    margin: 0,
    [DESKTOP]: { fontSize: "17px", lineHeight: "29px" },
  },

  /* ----------------------------------------------------------- identifier */
  identifier: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    borderTop: `1px solid ${RULE}`,
    borderBottom: `1px solid ${RULE}`,
    padding: "16px 0",
    marginTop: "6px",
    [DESKTOP]: { padding: "20px 0" },
  },
  identifierLabel: {
    fontFamily: MONO,
    fontSize: "10px",
    fontWeight: 500,
    letterSpacing: "0.14em",
    lineHeight: "14px",
    color: MUTED,
  },
  identifierValue: {
    fontFamily: MONO,
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "24px",
    color: INK,
    margin: 0,
    wordBreak: "break-word",
    [DESKTOP]: { fontSize: "19px", lineHeight: "28px" },
  },
  identifierName: {
    fontFamily: BODY,
    fontSize: "18px",
    fontWeight: 500,
    lineHeight: "26px",
    color: INK,
    margin: 0,
    [DESKTOP]: { fontSize: "21px", lineHeight: "30px" },
  },

  /* ----------------------------------------------------------- next steps */
  // The old page told the reader to "contact customer support" and gave no
  // address, and offered no route to the public search — which is exactly
  // what someone holding a damaged code needs.
  next: {
    background: SHEET,
    borderTop: `1px solid ${RULE}`,
  },
  nextInner: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    padding: "34px 0 40px",
    [DESKTOP]: { padding: "56px 0 64px", gap: "28px" },
  },
  nextTitle: {
    fontFamily: DISPLAY,
    fontSize: "25px",
    fontWeight: 500,
    letterSpacing: "-0.015em",
    lineHeight: "30px",
    color: INK,
    margin: 0,
    [DESKTOP]: { fontSize: "30px", lineHeight: "36px" },
  },
  cards: {
    display: "flex",
    flexDirection: "column",
    gap: "0px",
    [DESKTOP]: { flexDirection: "row", gap: "56px", alignItems: "flex-start" },
  },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    borderTop: `1px solid ${RULE}`,
    padding: "20px 0",
    [DESKTOP]: { flex: "1 1 0", padding: "22px 0 0", maxWidth: "440px" },
  },
  cardTitle: {
    fontFamily: BODY,
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: "22px",
    color: INK,
    margin: 0,
  },
  cardBody: {
    fontFamily: BODY,
    fontSize: "15px",
    fontWeight: 400,
    lineHeight: "23px",
    color: MUTED,
    margin: 0,
  },
  cardAction: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "flex-start",
    background: GREEN,
    color: SHEET,
    textDecoration: "none",
    padding: "13px 22px",
    marginTop: "4px",
    fontFamily: BODY,
    fontSize: "15px",
    fontWeight: 600,
    lineHeight: "20px",
    "&:hover": { background: GREEN_HOVER, color: SHEET },
  },
  cardEmail: {
    alignSelf: "flex-start",
    marginTop: "4px",
    fontFamily: MONO,
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    color: GREEN,
    textDecoration: "underline",
  },

};

export default invalidCertStyle;
